import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Users, RefreshCw, Pencil, ScrollText, Trash2, PlugZap, Unplug, KeyRound,
  ShieldCheck, TriangleAlert, Snowflake, CircleSlash, Briefcase,
} from "lucide-react";
import { EditAccountDialog } from "./accounts/EditAccountDialog";
import {
  DataTable, EmptyState, FilterSelect, IconBtn, MiniStat, PageHead, PageShell,
  Pagination, Pill, Row, SearchInput, StatStrip, Td, Toolbar,
  relativeTime, toneOf, usePagedRows,
} from "@/components/ui/kit";

const COLUMNS = [
  { key: "account", label: "Account" },
  { key: "phone", label: "Phone", hide: "hidden md:table-cell" },
  { key: "username", label: "Username", hide: "hidden lg:table-cell" },
  { key: "conn", label: "Connection" },
  { key: "jobs", label: "Jobs", align: "right", hide: "hidden md:table-cell" },
  { key: "health", label: "Health", hide: "hidden lg:table-cell" },
  { key: "seen", label: "Last Activity", align: "right", hide: "hidden xl:table-cell" },
  { key: "actions", label: "Actions", align: "right" },
];

/** One vocabulary for account state, shared by the counts and the table. */
const bucketOf = (a) => {
  if (a.session_error || a.needs_reverification) return "frozen";
  if (a.reconnecting) return "limited";
  if (a.status === "connected") return "connected";
  return "disconnected";
};

const BUCKET = {
  connected: { label: "Connected", tone: "var(--c-emerald)" },
  limited: { label: "Limited", tone: "var(--c-amber)" },
  frozen: { label: "Frozen", tone: "var(--c-rose)" },
  disconnected: { label: "Disconnected", tone: "var(--tg-faint)" },
};

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newAcc, setNewAcc] = useState({ label: "", api_id: "", api_hash: "", phone: "" });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", phone: "", api_id: "", api_hash: "" });
  const [codeFor, setCodeFor] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [needsPw, setNeedsPw] = useState(false);
  const [logsFor, setLogsFor] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const load = async () => {
    const [a, j] = await Promise.all([api.get("/accounts"), api.get("/jobs")]);
    setAccounts(a.data);
    setJobs(j.data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const addAccount = async () => {
    if (!newAcc.label?.trim()) return toast.error("Enter a label for this account");
    if (!newAcc.api_id || !newAcc.api_hash || !newAcc.phone) return toast.error("Fill all fields");
    try {
      const payload = { label: newAcc.label.trim(), api_id: newAcc.api_id, api_hash: newAcc.api_hash, phone: newAcc.phone };
      const { data } = await api.post("/accounts", payload);
      await api.post(`/accounts/${data.id}/connect`, {});
      toast.success("Code sent to Telegram app");
      setAddOpen(false);
      setNewAcc({ label: "", api_id: "", api_hash: "", phone: "" });
      setCodeFor(data.id);
      setNeedsPw(false);
      setCode("");
      setPassword("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to add account"));
    }
  };

  const openEdit = (a) => {
    setEditFor(a.id);
    setEditForm({ label: a.label || "", phone: a.phone || "", api_id: a.api_id != null ? String(a.api_id) : "", api_hash: "" });
  };
  const saveEdit = async () => {
    if (!editForm.label.trim()) return toast.error("Label cannot be empty");
    try {
      const payload = { label: editForm.label.trim(), phone: editForm.phone.trim() || null, api_id: editForm.api_id.trim() || null };
      if (editForm.api_hash.trim()) payload.api_hash = editForm.api_hash.trim();
      await api.put(`/accounts/${editFor}`, payload);
      toast.success("Account updated");
      setEditFor(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to update account"));
    }
  };

  const resendCode = async (id) => {
    try {
      await api.post(`/accounts/${id}/connect`, {});
      setCodeFor(id);
      setNeedsPw(false);
      setCode("");
      toast.success("Login code sent");
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    }
  };

  const verify = async () => {
    if (!code) return toast.error("Enter the code");
    try {
      const { data } = await api.post(`/accounts/${codeFor}/verify`, { code, password: password || null });
      if (data.needs_password) {
        setNeedsPw(true);
        toast.info("2FA password required");
      } else if (data.authorized) {
        toast.success("Account connected");
        setCodeFor(null);
        load();
      }
    } catch (e) {
      toast.error(errMsg(e, "Verification failed"));
    }
  };

  const disconnect = async (id) => {
    try {
      await api.post(`/accounts/${id}/disconnect`);
      toast.success("Disconnected — session kept, reconnect anytime");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Disconnect failed"));
    }
  };
  const reconnect = async (id) => {
    try {
      await api.post(`/accounts/${id}/reconnect`);
      toast.success("Reconnecting from saved session…");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Reconnect failed"));
    }
  };
  const remove = async (id) => {
    const acc = accounts.find((a) => a.id === id);
    if (!window.confirm(`Remove "${acc?.label || id}"? This logs the session out and deletes the account record.`)) return;
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Account removed");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Remove failed"));
    }
  };

  const refreshConnections = async () => {
    setReloading(true);
    try {
      const { data } = await api.post("/accounts/reload");
      toast.success(`Re-synced ${data.count} account${data.count === 1 ? "" : "s"} into the active worker`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to refresh connections"));
    } finally {
      setReloading(false);
    }
  };

  const jobCount = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      new Set([j.source_account_id || j.account_id, j.dest_account_id || j.account_id])
        .forEach((id) => { if (id) map[id] = (map[id] || 0) + 1; });
    });
    return map;
  }, [jobs]);

  const counts = useMemo(() => {
    const c = { connected: 0, disconnected: 0, limited: 0, frozen: 0 };
    accounts.forEach((a) => { c[bucketOf(a)] += 1; });
    return c;
  }, [accounts]);

  const assignedTotal = useMemo(
    () => accounts.reduce((n, a) => n + (jobCount[a.id] ? 1 : 0), 0), [accounts, jobCount]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (bucketFilter !== "all" && bucketOf(a) !== bucketFilter) return false;
      if (!q) return true;
      return [a.label, a.phone, a.me?.username, a.me?.name]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
    });
  }, [accounts, search, bucketFilter]);

  useEffect(() => { setPage(0); }, [search, bucketFilter, pageSize]);
  const paged = usePagedRows(filtered, { page, pageSize });

  const logsAcc = accounts.find((a) => a.id === logsFor);
  const codeAcc = accounts.find((a) => a.id === codeFor);
  const pick = (k) => setBucketFilter((c) => (c === k ? "all" : k));

  return (
    <PageShell>
      <StatStrip cols="sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-6">
        <MiniStat label="Connected" value={counts.connected} icon={ShieldCheck} accent="var(--c-emerald)" tint="var(--t-emerald)"
                  testid="acc-count-connected" onClick={() => pick("connected")} active={bucketFilter === "connected"} />
        <MiniStat label="Disconnected" value={counts.disconnected} icon={Unplug} accent="var(--tg-faint)" tint="rgba(140,147,166,.12)"
                  testid="acc-count-disconnected" onClick={() => pick("disconnected")} active={bucketFilter === "disconnected"} />
        <MiniStat label="Limited" value={counts.limited} icon={CircleSlash} accent="var(--c-amber)" tint="var(--t-amber)"
                  testid="acc-count-limited" onClick={() => pick("limited")} active={bucketFilter === "limited"} />
        <MiniStat label="Frozen" value={counts.frozen} icon={Snowflake} accent="var(--c-rose)" tint="var(--t-rose)"
                  testid="acc-count-frozen" onClick={() => pick("frozen")} active={bucketFilter === "frozen"} />
        <MiniStat label="Total" value={accounts.length} icon={Users} accent="var(--c-sky)" tint="var(--t-sky)" testid="acc-count-total" />
        <MiniStat label="Assigned Jobs" value={assignedTotal} icon={Briefcase} accent="var(--c-violet)" tint="var(--t-violet)" testid="acc-count-assigned" />
      </StatStrip>

      <PageHead title="Accounts" count={filtered.length}>
        <Button size="sm" variant="outline" onClick={refreshConnections} disabled={reloading}
                className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] font-semibold"
                data-testid="refresh-connections-btn"
                title="Re-sync all accounts from the database into the running worker">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${reloading ? "animate-spin" : ""}`} /> Refresh connections
        </Button>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="add-account-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-head">Add Telegram Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="label-caps">Label</Label>
                <Input data-testid="acc-name" value={newAcc.label} onChange={(e) => setNewAcc({ ...newAcc, label: e.target.value })} placeholder="e.g. Main account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]" />
                <p className="text-[11px] text-[var(--tg-faint)]">Your name for this account (like a contact). Shown everywhere; never changed by Telegram.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-caps">API ID</Label>
                  <Input data-testid="acc-api-id" value={newAcc.api_id} onChange={(e) => setNewAcc({ ...newAcc, api_id: e.target.value })} className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Phone</Label>
                  <Input data-testid="acc-phone" value={newAcc.phone} onChange={(e) => setNewAcc({ ...newAcc, phone: e.target.value })} placeholder="+15551234567" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">API Hash</Label>
                <Input data-testid="acc-api-hash" value={newAcc.api_hash} onChange={(e) => setNewAcc({ ...newAcc, api_hash: e.target.value })} className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" />
              </div>
              <p className="text-xs text-[var(--tg-faint)]">Get credentials at my.telegram.org → API development tools.</p>
              <Button onClick={addAccount} className="w-full bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="acc-send-code">
                Add &amp; Send Login Code
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </PageHead>

      <Toolbar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search label, phone, username…" testid="accounts-search" />
        <FilterSelect
          value={bucketFilter} onValueChange={setBucketFilter} width="w-40" testid="accounts-state-filter"
          options={[
            { value: "all", label: "All accounts" },
            { value: "connected", label: "Connected" },
            { value: "disconnected", label: "Disconnected" },
            { value: "limited", label: "Limited" },
            { value: "frozen", label: "Frozen" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={COLUMNS}
        testid="accounts-table"
        minWidth="min-w-[820px]"
        empty={
          paged.slice.length === 0 ? (
            <EmptyState
              icon={Users}
              testid="no-accounts"
              title={accounts.length === 0 ? "No accounts yet" : "No accounts match these filters"}
              hint={accounts.length === 0 ? "Add a Telegram user account to power your jobs." : undefined}
            />
          ) : null
        }
      >
        {paged.slice.map((a) => {
          const bucket = bucketOf(a);
          const meta = BUCKET[bucket];
          const needsAttention = bucket === "frozen";
          return (
            <Row key={a.id} testid={`account-${a.id}`} tone={needsAttention ? "var(--c-rose)" : undefined}>
              <Td>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate max-w-[180px]" title={a.label}>{a.label}</div>
                  {a.me?.name && (
                    <div className="text-[11px] text-[var(--tg-faint)] truncate max-w-[180px]" title={a.me.name}
                         data-testid={`account-tg-meta-${a.id}`}>{a.me.name}</div>
                  )}
                </div>
              </Td>
              <Td className="hidden md:table-cell font-mono text-xs text-[var(--tg-muted)] whitespace-nowrap">{a.phone || "—"}</Td>
              <Td className="hidden lg:table-cell font-mono text-xs text-[var(--tg-muted)]">
                <span className="truncate max-w-[140px] inline-block align-middle" title={a.me?.username ? `@${a.me.username}` : "—"}>
                  {a.me?.username ? `@${a.me.username}` : "—"}
                </span>
              </Td>
              <Td>
                <Pill tone={meta.tone} dot={bucket === "connected"} testid={`account-status-${a.id}`}>{meta.label}</Pill>
              </Td>
              <Td align="right" className="hidden md:table-cell tabular-nums text-sm">{jobCount[a.id] || 0}</Td>
              <Td className="hidden lg:table-cell">
                {a.last_error
                  ? <span className="inline-flex items-center gap-1 text-[11px] text-[var(--c-amber)] truncate max-w-[170px]" title={a.last_error}>
                      <TriangleAlert className="h-3 w-3 shrink-0" /> {a.last_error}
                    </span>
                  : <span className="inline-flex items-center gap-1 text-[11px] text-[var(--c-emerald)]"><ShieldCheck className="h-3 w-3" /> OK</span>}
              </Td>
              <Td align="right" className="hidden xl:table-cell text-[11px] text-[var(--tg-faint)] whitespace-nowrap"
                  title={a.last_seen ? new Date(a.last_seen).toLocaleString() : undefined}>
                {relativeTime(a.last_seen) || "—"}
              </Td>
              <Td align="right">
                <div className="flex items-center justify-end gap-1">
                  {a.status === "connected"
                    ? <IconBtn icon={Unplug} title="Disconnect (keeps session)" tone="var(--c-amber)" onClick={() => disconnect(a.id)} testid={`disconnect-${a.id}`} />
                    : a.has_session
                      ? <IconBtn icon={PlugZap} title="Reconnect from saved session" tone="var(--c-emerald)" onClick={() => reconnect(a.id)} testid={`reconnect-${a.id}`} />
                      : null}
                  <IconBtn icon={KeyRound} title="Send login code" tone="var(--c-sky)" onClick={() => resendCode(a.id)} testid={`connect-${a.id}`} />
                  <IconBtn icon={ScrollText} title="Connection log" onClick={() => setLogsFor(a.id)} testid={`logs-${a.id}`} />
                  <IconBtn icon={Pencil} title="Edit account" onClick={() => openEdit(a)} testid={`edit-${a.id}`} />
                  <IconBtn icon={Trash2} title="Remove account" tone="var(--c-rose)" onClick={() => remove(a.id)} testid={`remove-${a.id}`} />
                </div>
              </Td>
            </Row>
          );
        })}
      </DataTable>

      {filtered.length > pageSize && (
        <Pagination
          page={paged.safePage} pageCount={paged.pageCount} total={paged.total}
          from={paged.from} to={paged.to} pageSize={pageSize} onPageSize={setPageSize}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(paged.pageCount - 1, p + 1))}
          testid="accounts-pagination"
        />
      )}

      {/* login-code verification */}
      <Dialog open={!!codeFor} onOpenChange={(v) => { if (!v) { setCodeFor(null); setNeedsPw(false); setCode(""); setPassword(""); } }}>
        <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Verify {codeAcc?.label || "account"}</DialogTitle>
            <DialogDescription className="text-[var(--tg-faint)]">
              Enter the login code Telegram sent to this account's app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div className="space-y-2">
              <Label className="label-caps">Login code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="12345"
                     className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" data-testid="acc-code" />
            </div>
            {needsPw && (
              <div className="space-y-2">
                <Label className="label-caps">2FA password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono" data-testid="acc-2fa" />
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={verify} className="flex-1 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="acc-verify">
                Verify &amp; Connect
              </Button>
              <Button variant="outline" onClick={() => resendCode(codeFor)} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]">
                Resend
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!logsFor} onOpenChange={(v) => !v && setLogsFor(null)}>
        <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Connection Log — {logsAcc?.label}</DialogTitle>
            <DialogDescription className="text-[var(--tg-faint)]">
              Recent connection and session events for this account.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[var(--tg-bg)] border border-[var(--tg-border)] rounded-md p-4 max-h-96 overflow-y-auto font-mono text-xs space-y-1" data-testid="account-conn-logs">
            {(!logsAcc?.conn_logs || logsAcc.conn_logs.length === 0) && <p className="text-[var(--tg-faint)]">No connection events yet.</p>}
            {(logsAcc?.conn_logs || []).slice().reverse().map((l, i) => (
              <div key={`${l.ts}-${i}`} className="text-[var(--tg-muted)]">
                <span className="text-[var(--tg-faint)]">{new Date(l.ts).toLocaleTimeString()} </span>
                {l.text}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <EditAccountDialog
        open={!!editFor}
        onOpenChange={(v) => !v && setEditFor(null)}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={saveEdit}
      />
    </PageShell>
  );
}
